/**
 * Minimal RIS parser — extracts title, DOI, authors, and year from RIS text.
 *
 * RIS is a line-based format:
 *   TY  - JOUR
 *   AU  - Smith, John
 *   TI  - Some Paper Title
 *   DO  - 10.1234/example
 *   PY  - 2023
 *   ER  -
 */

export interface RisEntry {
  title: string;
  doi: string | null;
  authors: string[];
  year: number | null;
}

/** Returns true if the text looks like RIS format. */
export function isRisText(text: string): boolean {
  return /^TY\s\s-\s/m.test(text);
}

/** Returns true if the text looks like BibTeX format. */
export function isBibtexText(text: string): boolean {
  return /^@\w+\s*\{/m.test(text);
}

/** Parse RIS text into entries. */
export function parseRis(text: string): RisEntry[] {
  const entries: RisEntry[] = [];
  let current: { titles: string[]; doi: string | null; authors: string[]; year: number | null } | null = null;

  for (const line of text.split(/\r?\n/)) {
    const match = line.match(/^([A-Z][A-Z0-9])\s\s-\s(.*)$/);
    if (!match) continue;

    const [, tag, value] = match;
    const trimmed = value.trim();

    if (tag === "TY") {
      current = { titles: [], doi: null, authors: [], year: null };
    } else if (tag === "ER") {
      if (current && current.titles.length > 0) {
        entries.push({
          title: current.titles[0],
          doi: current.doi,
          authors: current.authors,
          year: current.year,
        });
      }
      current = null;
    } else if (current) {
      switch (tag) {
        case "TI":
        case "T1":
          current.titles.push(trimmed);
          break;
        case "DO":
          current.doi = trimmed.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
          break;
        case "AU":
        case "A1":
          current.authors.push(trimmed);
          break;
        case "PY":
        case "Y1":
        case "DA": {
          const yearMatch = trimmed.match(/^(\d{4})/);
          if (yearMatch) current.year = parseInt(yearMatch[1], 10);
          break;
        }
      }
    }
  }

  // Handle unterminated last entry
  if (current && current.titles.length > 0) {
    entries.push({
      title: current.titles[0],
      doi: current.doi,
      authors: current.authors,
      year: current.year,
    });
  }

  return entries;
}

/**
 * Try to extract a paper title from unstructured citation text.
 * Heuristic: longest sentence-like segment, often after a year pattern.
 */
export function extractTitleFromCitation(text: string): string | null {
  // Try to find text after a year pattern like "(2023)" or "2023."
  const afterYear = text.match(/(?:\(?\d{4}\)?[\.\,\s]+)([^\.]+)/);
  if (afterYear && afterYear[1].trim().length > 10) {
    return afterYear[1].trim().replace(/[,.]$/, "");
  }

  // Fallback: longest segment between periods
  const segments = text.split(/\.\s+/).filter((s) => s.length > 15);
  if (segments.length > 0) {
    return segments.reduce((a, b) => (a.length > b.length ? a : b)).trim();
  }

  return text.trim().length > 5 ? text.trim() : null;
}
